import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, FileText, Printer, Search, X, FileBarChart, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { localDateISO } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const PRESETS = [
  ["this_month", "This Month"],
  ["last_month", "Last Month"],
  ["last_6_months", "Last 6 Months"],
  ["this_year", "Current Year"],
  ["last_year", "Previous Year"],
  ["custom", "Custom Date Range"],
];

function presetRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const iso = localDateISO;
  switch (preset) {
    case "this_month": return [iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))];
    case "last_month": return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
    case "last_6_months": return [iso(new Date(y, m - 5, 1)), iso(now)];
    case "this_year": return [`${y}-01-01`, `${y}-12-31`];
    case "last_year": return [`${y - 1}-01-01`, `${y - 1}-12-31`];
    default: return ["", ""];
  }
}

const monthLabel = (ym) => {
  if (!ym) return "—";
  const d = new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

export default function Reports() {
  const [meta, setMeta] = useState(null);
  const [kind, setKind] = useState("income");
  const [category, setCategory] = useState("");
  const [preset, setPreset] = useState("this_year");
  const [from, setFrom] = useState(() => presetRange("this_year")[0]);
  const [to, setTo] = useState(() => presetRange("this_year")[1]);
  const [search, setSearch] = useState("");
  const [pledgeNo, setPledgeNo] = useState("");
  const [view, setView] = useState("transactions");
  const [familyFilter, setFamilyFilter] = useState(null);
  const [txns, setTxns] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(false);
  const firstRun = useRef(true);

  const categories = meta ? (kind === "income" ? meta.income_categories : meta.expense_categories) : [];
  const isPledgeCategory = /pledge/i.test(category || "");

  const params = useMemo(() => {
    const p = { kind, date_from: from, date_to: to };
    if (category) p.category = category;
    if (familyFilter?.family_id) p.family_id = familyFilter.family_id;
    else if (search.trim()) p.search = search.trim();
    if (pledgeNo.trim()) p.pledge_no = pledgeNo.trim();
    return p;
  }, [kind, category, from, to, search, pledgeNo, familyFilter]);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      if (view === "monthly") {
        const res = await api.get("/admin/reports/monthly", { params });
        setMonthly(res.data);
      } else if (view === "family") {
        const res = await api.get("/admin/reports/family-summary", { params });
        setFamily(res.data);
        if (familyFilter) {
          const t = await api.get("/admin/reports/transactions", { params });
          setTxns(t.data);
        }
      } else {
        const res = await api.get("/admin/reports/transactions", { params });
        setTxns(res.data);
      }
    } catch (err) {
      toast.error(apiError(err, "Could not generate report"));
    } finally {
      setLoading(false);
    }
  }, [params, view, familyFilter]);

  useEffect(() => {
    api.get("/admin/reports/meta").then((res) => setMeta(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(generate, firstRun.current ? 0 : 400);
    firstRun.current = false;
    return () => clearTimeout(t);
  }, [generate]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p !== "custom") {
      const [f, t] = presetRange(p);
      setFrom(f);
      setTo(t);
    }
  };

  const clearFilters = () => {
    setCategory(""); setSearch(""); setPledgeNo(""); setFamilyFilter(null);
    setPreset("this_year");
    const [f, t] = presetRange("this_year");
    setFrom(f); setTo(t);
    setView("transactions");
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/admin/reports/export.${fmt}`, {
        params: { ...params, view: view === "family" ? "family" : "transactions" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}_report.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} report downloaded`);
    } catch (err) {
      toast.error(apiError(err, "Export failed"));
    }
  };

  const drillFamily = (f) => {
    setFamilyFilter({ family_id: f.family_id || "", name: f.name });
  };

  const summaryTotal = view === "monthly" ? monthly?.total_amount : view === "family" ? family?.grand_total : txns?.total_amount;
  const summaryCount = view === "monthly" ? monthly?.count : view === "family" ? family?.count : txns?.count;
  const rangeLabel = from && to ? `${from} → ${to}` : "All dates";

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy">Reports</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Generated live from Church Ledger transactions — totals always match the ledger.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => download("csv")} data-testid="reports-export-csv">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="reports-export-pdf">
            <FileText className="mr-2 h-4 w-4" /> Export PDF
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="reports-print-btn">
            <Printer className="mr-2 h-4 w-4" /> Print Report
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white p-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label>Report Type</Label>
            <Tabs value={kind} onValueChange={(v) => { setKind(v); setCategory(""); setView("transactions"); setFamilyFilter(null); }} className="mt-1.5">
              <TabsList className="w-full">
                <TabsTrigger value="income" className="flex-1" data-testid="reports-type-income">Income</TabsTrigger>
                <TabsTrigger value="expense" className="flex-1" data-testid="reports-type-expense">Expense</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category || "all"} onValueChange={(v) => { setCategory(v === "all" ? "" : v); setFamilyFilter(null); if (!/pledge/i.test(v)) setView((cur) => (cur === "family" ? "transactions" : cur)); }}>
              <SelectTrigger className="mt-1.5" data-testid="reports-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="reports-category-all">All Categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c} data-testid={`reports-category-${c.replace(/\s+/g, "-")}`}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date Range</Label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger className="mt-1.5" data-testid="reports-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESETS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`reports-preset-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {preset === "custom" ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="rep-from">From Date</Label>
                <Input id="rep-from" type="date" className="mt-1.5" value={from} data-testid="reports-from"
                  onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="rep-to">To Date</Label>
                <Input id="rep-to" type="date" className="mt-1.5" value={to} data-testid="reports-to"
                  onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
          ) : (
            <div>
              <Label>Search (family / donor / receipt / description)</Label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" value={search} data-testid="reports-search" placeholder="e.g. Alexander"
                  onChange={(e) => { setSearch(e.target.value); setFamilyFilter(null); }} />
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          {preset === "custom" && (
            <div className="min-w-64 flex-1">
              <Label>Search (family / donor / receipt / description)</Label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" value={search} data-testid="reports-search-custom" placeholder="e.g. Alexander"
                  onChange={(e) => { setSearch(e.target.value); setFamilyFilter(null); }} />
              </div>
            </div>
          )}
          {isPledgeCategory && (
            <div>
              <Label>Pledge No.</Label>
              <Input className="mt-1.5 w-32" value={pledgeNo} data-testid="reports-pledge-no" placeholder="e.g. 37"
                list="pledge-nos" onChange={(e) => setPledgeNo(e.target.value)} />
              <datalist id="pledge-nos">
                {(meta?.pledge_numbers || []).map((p) => <option key={p} value={p} />)}
              </datalist>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={generate} disabled={loading} className="bg-navy text-cream hover:bg-navy-light" data-testid="reports-generate-btn">
              <FileBarChart className="mr-2 h-4 w-4" /> {loading ? "Generating…" : "Generate Report"}
            </Button>
            <Button variant="outline" onClick={clearFilters} data-testid="reports-clear-btn">
              <X className="mr-2 h-4 w-4" /> Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-lg border border-border bg-white p-4" data-testid="reports-card-total">
          <p className={`text-xl font-bold ${kind === "income" ? "text-green-700" : "text-red-700"}`}>{inr(summaryTotal)}</p>
          <p className="text-xs text-muted-foreground">Total {kind === "income" ? "Income" : "Expenses"}</p>
        </div>
        <div className="rounded-lg border border-border bg-white p-4" data-testid="reports-card-transactions">
          <p className="text-xl font-bold text-navy">{summaryCount ?? 0}</p>
          <p className="text-xs text-muted-foreground">Transactions</p>
        </div>
        <div className="rounded-lg border border-border bg-white p-4" data-testid="reports-card-range">
          <p className="text-sm font-semibold text-navy">{rangeLabel}</p>
          <p className="text-xs text-muted-foreground">Selected Date Range</p>
        </div>
        <div className="rounded-lg border border-border bg-white p-4" data-testid="reports-card-category">
          <p className="truncate text-sm font-semibold text-navy">{category || "All Categories"}</p>
          <p className="text-xs text-muted-foreground">Selected Category</p>
        </div>
      </div>

      <Tabs value={view} onValueChange={(v) => { setView(v); if (v !== "family") setFamilyFilter(null); }}>
        <TabsList>
          <TabsTrigger value="transactions" data-testid="reports-view-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="reports-view-monthly">Monthly Summary</TabsTrigger>
          {isPledgeCategory && <TabsTrigger value="family" data-testid="reports-view-family">Family Summary</TabsTrigger>}
        </TabsList>
      </Tabs>

      <div className="print-report-area rounded-lg border border-border bg-white p-4" data-testid="reports-result">
        <div className="mb-4 text-center">
          <h2 className="font-heading text-lg font-bold text-navy">Christ Church CNI Rajpura</h2>
          <p className="text-xs text-muted-foreground">Chandigarh Diocese</p>
          <p className="mt-1 text-sm font-semibold" data-testid="reports-title">
            {kind === "income" ? "Income" : "Expense"} Report — {category || "All Categories"}
          </p>
          <p className="text-xs text-muted-foreground">
            Period: {rangeLabel}
            {familyFilter ? ` | Family: ${familyFilter.name}` : search.trim() ? ` | Search: ${search.trim()}` : ""}
            {pledgeNo.trim() ? ` | Pledge No: ${pledgeNo.trim()}` : ""}
          </p>
        </div>

        {view === "transactions" && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reports-table">
              <thead>
                <tr className="border-b border-border bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Category</th>
                  {isPledgeCategory && <th className="px-3 py-2">Pledge No</th>}
                  <th className="px-3 py-2">Receipt / Ref No</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">{kind === "income" ? "Donor/Payer" : "Payee"}</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(txns?.items || []).map((e) => (
                  <tr key={e.id} className="border-b border-border/60 last:border-0" data-testid={`reports-row-${e.id}`}>
                    <td className="whitespace-nowrap px-3 py-2">{e.date}</td>
                    <td className="px-3 py-2">{e.category || "—"}</td>
                    {isPledgeCategory && <td className="px-3 py-2">{e.pledge_no || "—"}</td>}
                    <td className="px-3 py-2">{e.ref_no || "—"}</td>
                    <td className="max-w-56 truncate px-3 py-2">{e.description || "—"}</td>
                    <td className="px-3 py-2">{e.party || "—"}</td>
                    <td className="px-3 py-2 text-right font-medium">{inr(e.amount)}</td>
                  </tr>
                ))}
                {!txns?.items?.length && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="reports-empty">
                    {loading ? "Generating…" : "No transactions match the selected filters."}
                  </td></tr>
                )}
              </tbody>
              {txns?.items?.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-navy/20 bg-secondary/40 font-semibold">
                    <td className="px-3 py-2" colSpan={isPledgeCategory ? 6 : 5}>Total ({txns.count} transactions)</td>
                    <td className="px-3 py-2 text-right" data-testid="reports-total">{inr(txns.total_amount)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {view === "monthly" && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reports-monthly-table">
              <thead>
                <tr className="border-b border-border bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Month</th>
                  <th className="px-3 py-2 text-right">Transactions</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(monthly?.months || []).map((m) => (
                  <tr key={m.month} className="border-b border-border/60 last:border-0" data-testid={`reports-month-${m.month}`}>
                    <td className="px-3 py-2">{monthLabel(m.month)}</td>
                    <td className="px-3 py-2 text-right">{m.count}</td>
                    <td className="px-3 py-2 text-right">{m.count ? inr(m.total) : "₹0"}</td>
                  </tr>
                ))}
              </tbody>
              {monthly && (
                <tfoot>
                  <tr className="border-t-2 border-navy/20 bg-secondary/40 font-semibold">
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-right">{monthly.count}</td>
                    <td className="px-3 py-2 text-right" data-testid="reports-monthly-total">{inr(monthly.total_amount)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {view === "family" && family && !familyFilter && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reports-family-table">
              <thead>
                <tr className="border-b border-border bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Family</th>
                  <th className="px-3 py-2">Pledge No</th>
                  {family.months.map((m) => <th key={m} className="px-3 py-2 text-right">{monthLabel(m)}</th>)}
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {family.families.map((f) => (
                  <tr key={f.family_id || f.name} className="border-b border-border/60 last:border-0" data-testid={`reports-family-row-${f.name.replace(/\s+/g, "-")}`}>
                    <td className="px-3 py-2">
                      <button type="button" className="font-medium text-navy underline-offset-2 hover:underline"
                        data-testid={`reports-family-drill-${f.name.replace(/\s+/g, "-")}`} onClick={() => drillFamily(f)}>
                        {f.name}
                      </button>
                    </td>
                    <td className="px-3 py-2">{f.pledge_no || "—"}</td>
                    {family.months.map((m) => (
                      <td key={m} className="px-3 py-2 text-right">{f.monthly[m] ? inr(f.monthly[m]) : "₹0"}</td>
                    ))}
                    <td className="px-3 py-2 text-right font-semibold">{inr(f.total)}</td>
                  </tr>
                ))}
                {!family.families.length && (
                  <tr><td colSpan={family.months.length + 3} className="px-3 py-8 text-center text-muted-foreground">No pledge transactions found for the selected filters.</td></tr>
                )}
              </tbody>
              {family.families.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-navy/20 bg-secondary/40 font-semibold">
                    <td className="px-3 py-2" colSpan={family.months.length + 2}>Total Pledge Received from All Families</td>
                    <td className="px-3 py-2 text-right" data-testid="reports-family-grand-total">{inr(family.grand_total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {view === "family" && family && familyFilter && (() => {
          const f = family.families[0];
          if (!f) return <p className="py-8 text-center text-muted-foreground" data-testid="reports-family-none">No transactions found for this family in the selected period.</p>;
          return (
            <div className="space-y-6" data-testid="reports-family-single">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading text-lg font-bold text-navy" data-testid="reports-family-name">Family: {f.name}</p>
                  <p className="text-sm text-muted-foreground" data-testid="reports-family-pledge-no">Pledge No.: {f.pledge_no || "—"}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setFamilyFilter(null)} data-testid="reports-family-back">
                  <ArrowLeft className="mr-2 h-4 w-4" /> All Families
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                {family.months.map((m) => (
                  <div key={m} className={`rounded-md border p-3 text-center ${f.monthly[m] ? "border-green-200 bg-green-50" : "border-border bg-secondary/40"}`} data-testid={`reports-family-month-${m}`}>
                    <p className="text-xs text-muted-foreground">{monthLabel(m)}</p>
                    <p className={`text-sm font-semibold ${f.monthly[m] ? "text-green-700" : "text-muted-foreground"}`}>
                      {f.monthly[m] ? inr(f.monthly[m]) : "₹0 / Not Paid"}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-6 rounded-md bg-secondary/50 p-4 text-sm">
                <span>Total Paid: <strong data-testid="reports-family-total">{inr(f.total)}</strong></span>
                <span>Number of Payments: <strong data-testid="reports-family-payments">{f.payments}</strong></span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reports-family-txns">
                  <thead>
                    <tr className="border-b border-border bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Receipt No</th>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(txns?.items || [])
                      .filter((e) => (f.family_id
                        ? e.family_id === f.family_id
                        : (e.party || "").trim().toLowerCase() === f.name.trim().toLowerCase()))
                      .map((e) => (
                      <tr key={e.id} className="border-b border-border/60 last:border-0" data-testid={`reports-family-txn-${e.id}`}>
                        <td className="whitespace-nowrap px-3 py-2">{e.date}</td>
                        <td className="px-3 py-2">{e.ref_no || "—"}</td>
                        <td className="px-3 py-2">{e.description || "—"}</td>
                        <td className="px-3 py-2 text-right font-medium">{inr(e.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
